import React, { useState, useEffect, useContext } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import SidebarClient from './SidebarClient';
import { AuthContext } from '../context/AuthContext';

export default function ClientDashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useContext(AuthContext);

  const [current, setCurrent] = useState('home');
  const [showVisitorInfo, setShowVisitorInfo] = useState(false);

  // Update current state based on route changes
  useEffect(() => {
    const path = location.pathname;
    if (path === '/client/home') {
      setCurrent('home');
    } else if (path === '/client/map') {
      setCurrent('map');
    } else if (path === '/client/profile') {
      setCurrent('profile');
    } else if (path.startsWith('/client/public-access/')) {
      setCurrent('public');
    } else if (path.startsWith('/client/reservations/') || path === '/client/columbarium') {
      setCurrent('booking');
    }
  }, [location.pathname]);

  // Pills
  const isVisitorInfoRoute = location.pathname === '/client/public-access/visitor-info';
  const isBookingSubpage = location.pathname.startsWith('/client/reservations/') || location.pathname === '/client/columbarium';

  const showVisitorInfoPill = current === 'public' && !showVisitorInfo && !isVisitorInfoRoute;
  const showBookingPills = current === 'booking' && !isBookingSubpage;

  // Main sidebar nav logic
  const handleNavigate = (target) => {
    console.clear(); // Clear the console to see navigation logs clearly
    console.log('=== NAVIGATION DEBUG ===');
    console.log('handleNavigate called with:', target, 'from:', location.pathname);
    setShowVisitorInfo(false);
    
    // Force a page reload when navigating away from the map to ensure clean navigation
    const isCurrentlyOnMap = location.pathname === '/client/map';
    
    if (target === 'home') {
      setCurrent('home');
      if (isCurrentlyOnMap) {
        console.log('Using page reload for map navigation');
        window.location.href = '/client/home';
      } else {
        navigate('/client/home');
      }
    } else if (target === 'map') {
      setCurrent('map');
      navigate('/client/map');
    } else if (target === 'profile') {
      setCurrent('profile');
      if (isCurrentlyOnMap) {
        console.log('Using page reload for map navigation');
        window.location.href = '/client/profile';
      } else {
        navigate('/client/profile');
      }
    } else if (target === 'public') {
      setCurrent('public');
      if (isCurrentlyOnMap) {
        console.log('Using page reload for map navigation');
        window.location.href = '/client/public-access/visitor-info';
      } else {
        navigate('/client/public-access/visitor-info');
      }
    } else if (target === 'booking') {
      setCurrent('booking');
      if (isCurrentlyOnMap) {
        console.log('Using page reload for map navigation');
        window.location.href = '/client/reservations/availability';
      } else {
        navigate('/client/reservations/availability');
      }
    }
  };

  // Visitor info
  const handleVisitorInfo = () => {
    setShowVisitorInfo(true);
    setCurrent('public');
    navigate('/client/public-access/visitor-info');
  };

  // Booking pills
  const handleBookingPill = (route) => {
    setCurrent('booking'); // Keep booking as current for highlighting
    const isCurrentlyOnMap = location.pathname === '/client/map';
    if (isCurrentlyOnMap) {
      console.log('Using page reload for booking pill navigation');
      window.location.href = `/client/reservations/${route}`;
    } else {
      navigate(`/client/reservations/${route}`);
    }
  };

  // Bookmark pill
  const handleBookmarkPill = (action) => {
    if (action === 'bookmark') {
      setCurrent('map'); // Keep map as current for highlighting
      const isCurrentlyOnMap = location.pathname === '/client/map';
      if (isCurrentlyOnMap) {
        console.log('Using page reload for bookmark pill navigation');
        window.location.href = '/client/bookmarks';
      } else {
        navigate('/client/bookmarks');
      }
    }
  };

  // Logout
  const handleLogout = () => {
    console.log('Client logout clicked');
    logout(); // Use AuthContext logout to properly clear state
    console.log('Navigating to login page');
    navigate('/login');
  };

  return (
    <div className="client-layout" style={{ display: 'flex' }}>
      <SidebarClient
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        current={current}
        showVisitorInfoPill={showVisitorInfoPill}
        onVisitorInfo={handleVisitorInfo}
        showBookingPills={showBookingPills}
        onBookingPill={handleBookingPill}
        onBookmarkPill={handleBookmarkPill}
      />
      <main style={{ flex: 1, padding: '1rem', paddingTop: '80px' }}>
        <Outlet key={location.pathname} context={{ handleNavigate, handleVisitorInfo, handleBookingPill, handleBookmarkPill }} />
      </main>
    </div>
  );
}
